import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import { translateManyClient } from "../../../utils/ItClient";
import i18next from "i18next";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// "data": [
//     {
//         "student_id": 2,
//         "full_name": "Ahmed Ali",
//         "student_street": "DHA Phase 6, Street 12",
//         "student_country": "United States",
//         "student_state": "Sindh",
//         "student_city": "Karachi",
//         "disability_notes": null,
//         "status": "pending",
//     },
// ]

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/parent`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken(); // Get token from cookies or storage
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

// Wrap the baseQuery with retry logic
// const baseQueryWithRetry = retry(baseQuery, {
//   maxRetries: 1,
//   retryCondition: (error, _args, { attempt }) => {
//     // sirf error hone par retry
//     return !!error && attempt <= 1;
//   },
// });

export const PraentSlice = createApi({
  reducerPath: "PraentSlice",
  baseQuery: baseQuery,
  tagTypes: ["parents"],
  endpoints: (builder) => ({
    // GET all Parents with filters
    getAllParents: builder.query({
      query: ({ page = 1, limit = 10, search, role }) => {
        const params = new URLSearchParams();

        params.append("page", page);
        params.append("limit", limit);

        if (search) params.append("search", search);
        if (role) params.append("role", role);

        return `?${params.toString()}`;
      },

      // dynamic localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18next.language || "en");
          if (lang === "en") return;

          const parentData = data?.data?.parents ?? [];
          if (!Array.isArray(parentData) || parentData.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const parent of parentData) {
            if (parent.full_name) bucket.push(parent.full_name);
            if (parent.email) bucket.push(parent.email);
            if (parent.state) bucket.push(parent.state);
            if (parent.street) bucket.push(parent.street);
            if (parent.city) bucket.push(parent.city);
            if (parent.country) bucket.push(parent.country);
            if (parent.status) bucket.push(parent.status);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            PraentSlice.util.updateQueryData("getAllParents", arg, (draft) => {
              const arr = draft?.data?.parents ?? [];
              arr.forEach((parent) => {
                parent.full_name = pick(parent.full_name);
                parent.email = pick(parent.email);
                parent.street = pick(parent.street);
                parent.state = pick(parent.state);
                parent.city = pick(parent.city);
                parent.country = pick(parent.country);
                parent.status = pick(parent.status);
              });
            }),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["parents"],
    }),
    getAllStudents: builder.query({
      query: ({ page = 1, limit = 10, search = "", status = "", parent_id = "", is_assigned = "" } = {}) => {
        const params = new URLSearchParams();

        params.append("page", page);
        params.append("limit", limit);

        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (parent_id) params.append("parent_id", parent_id);
        if (is_assigned !== "") params.append("is_assigned", is_assigned);

        return `students?${params.toString()}`;
      },
      providesTags: ["students"],
    }),
    // GET Praent by ID and child list
    getRecordDetails: builder.query({
      query: (endPoint) => endPoint,

      // dynamic localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18next.language || "en");
          if (lang === "en") return;

          const studentData = data?.data ?? [];
          if (!Array.isArray(studentData) || studentData.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const student of studentData) {
            if (student.full_name) bucket.push(student.full_name);
            if (student.status) bucket.push(student.status);
            if (student.student_street) bucket.push(student.student_street);
            if (student.student_city) bucket.push(student.student_city);
            if (student.student_state) bucket.push(student.student_state);
            if (student.student_country) bucket.push(student.student_country);
            if (student.disability_notes) bucket.push(student.disability_notes);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            PraentSlice.util.updateQueryData(
              "getRecordDetails",
              arg,
              (draft) => {
                const arr = draft?.data ?? [];
                arr.forEach((student) => {
                  student.full_name = pick(student.full_name);
                  student.status = pick(student.status);
                  student.student_street = pick(student.student_street);
                  student.student_city = pick(student.student_city);
                  student.student_state = pick(student.student_state);
                  student.student_country = pick(student.student_country);
                  student.disability_notes = pick(student.disability_notes);
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },
      providesTags: ["parents"],
    }),
    // Create New Parent with Child
    createRecord: builder.mutation({
      query: ({ data, endPoint }) => ({
        url: endPoint,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["parents"],
    }),
    // Update Praent
    updateRecord: builder.mutation({
      query: ({ data, endPoint }) => ({
        url: endPoint,
        method: "Put",
        body: data,
      }),
      invalidatesTags: ["parents"],
    }),
    // DELETE a Praent / Student
    deleteRecord: builder.mutation({
      query: (endPoint) => ({
        url: endPoint,
        method: "Delete",
      }),
      invalidatesTags: ["parents"],
    }),
  }),
});
// Export hooks for usage in components
export const {
  useGetAllParentsQuery, // use old
  useGetAllStudentsQuery, // new
  useGetRecordDetailsQuery, // use old
  useCreateRecordMutation, // use old
  useUpdateRecordMutation, // use old
  useDeleteRecordMutation,
} = PraentSlice;
